import { Injectable } from "@angular/core";
import { first } from "rxjs";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { AlertController, LoadingController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import * as fromApp from "../../store/app.reducer";
import * as selectors from "../../store/app.selectors";
import * as AuthActions from "../../auth/store/auth.actions";
import { DataStorageService } from "../data-storage/data-storage.service";
import { CheckLocationDistanceService } from "../checkLocationDistance/check-location-distance.service";
import { AnalyticsServiceService } from "../analitycsService/analytics-service.service";
import { DineinTableLabelService } from "../dinein-table-label/dinein-table-label.service";

type OnlyDineInLoginAssistStoreCache = {
  onlyDineInCapability: boolean | null;
  onlyDineInCapabilityFetchedAt: number | null;
  distanceToStoreMeters: number | null;
  distanceFetchedAt: number | null;
  gpsError: string | null;
  gpsDenied: boolean | null;
};

@Injectable({
  providedIn: "root",
})
export class OnlyDineInLoginAssistService {
  private capabilityInFlightByStoreId: Record<string, Promise<boolean> | null> =
    {};
  private gpsInFlightByStoreId: Record<string, Promise<void> | null> = {};
  private cacheByStoreId: Record<string, OnlyDineInLoginAssistStoreCache> = {};
  private allStoresGpsInFlight: Promise<void> | null = null;
  private guestReconnectInFlight: boolean = false;
  private guestReconnectLoading: any = null;

  private log(...args: any[]) {
    // Intentionally no-op (used to be guarded by a dev override flag).
  }

  constructor(
    private store: Store<fromApp.AppState>,
    private dataStorageService: DataStorageService,
    private checkLocationDistanceService: CheckLocationDistanceService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private translateService: TranslateService,
    private analyticsService: AnalyticsServiceService,
    private dineinTableLabelService: DineinTableLabelService
  ) {}

  prefetchOnlyDineInCapabilityForCurrentStore() {
    this.store
      .select(selectors.getStoreInfo)
      .pipe(first())
      .subscribe((storeInfo: any) => {
        this.log("prefetchOnlyDineInCapabilityForCurrentStore()", {
          store_id: storeInfo?.store_id,
          group: storeInfo?.group,
          dinein: storeInfo?.dinein,
        });
        void this.prefetchOnlyDineInCapabilityForStoreInfo(storeInfo);
      })
      .unsubscribe();
  }

  prefetchOnlyDineInCapabilityForStoreInfo(storeInfo: any) {
    if (
      !storeInfo ||
      storeInfo.dinein !== true ||
      storeInfo.notifyUserForOnlyDineinOnLogin !== true ||
      !storeInfo.store_id ||
      !storeInfo.group
    ) {
      this.log("skip prefetchOnlyDineInCapabilityForStoreInfo()", {
        store_id: storeInfo?.store_id,
        group: storeInfo?.group,
        dinein: storeInfo?.dinein,
        notifyUserForOnlyDineinOnLogin:
          storeInfo?.notifyUserForOnlyDineinOnLogin,
      });
      return;
    }

    this.log("prefetchOnlyDineInCapabilityForStoreInfo()", {
      store_id: storeInfo.store_id,
      group: storeInfo.group,
    });
    void this.prefetchOnlyDineInCapability(storeInfo);
  }

  prefetchLoginAssistForCurrentStore() {
    this.store
      .select("stores")
      .pipe(first())
      .subscribe((storesState: any) => {
        const stores = storesState?.stores || [];
        if (stores && stores.length) {
          this.log("prefetchLoginAssistForCurrentStore() -> all stores", {
            storesCount: stores?.length ?? 0,
          });
          this.prefetchLoginAssistForStores(stores);
          return;
        }

        this.store
          .select(selectors.getStoreInfo)
          .pipe(first())
          .subscribe((storeInfo) => {
            this.log("prefetchLoginAssistForCurrentStore() -> storeInfo fallback", {
              store_id: storeInfo?.store_id,
              group: storeInfo?.group,
              dinein: storeInfo?.dinein,
              notifyUserForOnlyDineinOnLogin:
                storeInfo?.notifyUserForOnlyDineinOnLogin,
              hasMapCenter:
                storeInfo?.mapCenterStoreLat != null &&
                storeInfo?.mapCenterStoreLng != null,
            });
            this.prefetchLoginAssistForStoreInfo(storeInfo);
          })
          .unsubscribe();
      })
      .unsubscribe();
  }

  prefetchLoginAssistForStores(stores: any[]) {
    if (!stores || !stores.length) {
      return;
    }

    const eligibleStoresForAttempt = _.filter(_.cloneDeep(stores), (s) =>
      this.shouldAttemptLoginAssistForStore(s),
    );

    if (!eligibleStoresForAttempt.length) {
      this.log("skip prefetchLoginAssistForStores()", {
        storesCount: stores?.length ?? 0,
      });
      return;
    }

    this.log("prefetchLoginAssistForStores() start", {
      storesCount: stores?.length ?? 0,
      attemptableStoresCount: eligibleStoresForAttempt.length,
    });

    void (async () => {
      await this.prefetchGpsDistancesToStores(eligibleStoresForAttempt, {
        maxAgeMs: 2 * 60 * 1000,
      });

      // Prefetch capability only for nearby candidates (best-effort).
      const candidates = this.getStoresWithinDistanceMeters(
        eligibleStoresForAttempt,
        250,
        { maxAgeMs: 2 * 60 * 1000 },
      );

      // Start from nearest to avoid calling the backend for many stores.
      candidates.forEach((storeInfo) => {
        void this.prefetchOnlyDineInCapability(storeInfo);
      });
    })();
  }

  prefetchLoginAssistForStoreInfo(storeInfo: any) {
    if (!this.shouldAttemptLoginAssistForStoreInfo(storeInfo)) {
      this.log("skip prefetchLoginAssistForStoreInfo()", {
        store_id: storeInfo?.store_id,
        group: storeInfo?.group,
        dinein: storeInfo?.dinein,
        notifyUserForOnlyDineinOnLogin:
          storeInfo?.notifyUserForOnlyDineinOnLogin,
        hasMapCenter:
          storeInfo?.mapCenterStoreLat != null &&
          storeInfo?.mapCenterStoreLng != null,
      });
      return;
    }

    this.log("prefetchLoginAssistForStoreInfo() start", {
      store_id: storeInfo?.store_id,
      group: storeInfo?.group,
    });
    void this.prefetchOnlyDineInCapability(storeInfo);
    void this.prefetchGpsDistanceToStore(storeInfo);
  }

  getSnapshotForStoreInfo(storeInfo: any): OnlyDineInLoginAssistStoreCache | null {
    const storeIdKey = this.getStoreIdKey(storeInfo);
    if (!storeIdKey) {
      return null;
    }

    return this.cacheByStoreId[storeIdKey] || null;
  }

  shouldShowContinueAsGuestForStoreInfo(
    storeInfo: any,
    opts?: { maxDistanceMeters?: number; maxAgeMs?: number },
  ): boolean {
    const maxDistanceMeters = opts?.maxDistanceMeters ?? 100;
    const maxAgeMs = opts?.maxAgeMs ?? 2 * 60 * 1000;

    if (!this.shouldAttemptLoginAssistForStoreInfo(storeInfo)) {
      return false;
    }

    const storeIdKey = this.getStoreIdKey(storeInfo);
    if (!storeIdKey) {
      return false;
    }

    const cache = this.cacheByStoreId[storeIdKey];
    if (!cache) {
      return false;
    }

    if (cache.onlyDineInCapability !== true) {
      return false;
    }

    if (cache.distanceToStoreMeters == null || cache.distanceFetchedAt == null) {
      return false;
    }

    if (Date.now() - cache.distanceFetchedAt > maxAgeMs) {
      return false;
    }

    return cache.distanceToStoreMeters <= maxDistanceMeters;
  }

  async getNearestEligibleStoreIdForContinueAsGuest(opts?: {
    maxDistanceMeters?: number;
    maxAgeMs?: number;
  }): Promise<any> {
    const maxDistanceMeters = opts?.maxDistanceMeters ?? 250;
    const maxAgeMs = opts?.maxAgeMs ?? 2 * 60 * 1000;

    let stores = await this.getStoresSnapshot();
    if (!stores || !stores.length) {
      const storeInfo = await this.getStoreInfoSnapshot();
      stores = storeInfo ? [storeInfo] : [];
    }
    if (!stores.length) {
      return null;
    }

    const attemptableStores = _.filter(_.cloneDeep(stores), (s) =>
      this.shouldAttemptLoginAssistForStore(s),
    );
    if (!attemptableStores.length) {
      return null;
    }

    await this.prefetchGpsDistancesToStores(attemptableStores, { maxAgeMs });

    const nearbyStores = this.getStoresWithinDistanceMeters(
      attemptableStores,
      maxDistanceMeters,
      { maxAgeMs },
    );

    for (const storeInfo of nearbyStores) {
      const capable = await this.prefetchOnlyDineInCapability(storeInfo);
      if (capable) {
        return storeInfo?.store_id ?? null;
      }
    }

    return null;
  }

  // Recovery for the dine-in guest dead-end. A do_not_navigate logout (the cart
  // LOGIN button, logoutOnlyDineInUser) clears the user and the token but keeps
  // the selected table on purpose, so a customer who abandons the login is left
  // with a table in the cart, no session, and — since auto re-auth is gated on a
  // single-table store — no way back except re-scanning the QR. This offers a
  // one-tap reconnect on the table that is already selected.
  //
  // Returns true ONLY when it fully handled the interaction (the guest is logged
  // in again). Every other path returns false so the caller runs its existing
  // login flow completely unchanged.
  // opts.extraMessage is appended to the alert body. It exists so a caller
  // can add a store-specific line — e.g. that card payment is open to guests
  // — without putting that promise into the shared alert copy, which is
  // served to every group while the payment audience is per store.
  // opts.ignoreSharedDismissal detaches a caller from the shared ten-minute
  // "not now". The surfaces ask different questions: the cart LOGIN button asks
  // whether to skip a login the customer has not attempted yet, while the
  // OTP-failure hook asks after that login has demonstrably failed. A "no" to
  // the first is stale once the second happens — and for a number the country
  // allowlist rejects, retyping it can never succeed, so staying silent there
  // silences exactly the population the offer exists for. Such a caller also
  // does not write the shared flag, so declining it leaves the other surfaces
  // alone; it throttles itself instead.
  async tryContinueAsGuestOnSelectedTable(opts?: {
    extraMessage?: string;
    ignoreSharedDismissal?: boolean;
  }): Promise<boolean> {
    // The flag has to cover the whole interaction, eligibility and alert
    // included. The cart CHECKOUT button stays tappable throughout — its
    // disabled binding keys on the price/address loading flags only — so
    // without this a second press stacks a second alert and mints a second
    // guest session. (The LOGIN button is additionally disabled from the moment
    // login_loading is dispatched.)
    if (this.guestReconnectInFlight) {
      return false;
    }
    this.guestReconnectInFlight = true;

    try {
      return await this.runGuestReconnectOffer(opts);
    } catch (e) {
      // Nothing may escape: both callers open the login screen immediately
      // after, so a throw here would leave the customer with a button that does
      // nothing at all. Same reasoning as reportTableKeptWithoutSession.
      // A throw after login_loading was dispatched would also leave that state
      // with nobody to release it.
      await this.releaseLoginStateIfStillLoggedOut();
      return false;
    } finally {
      // Dismiss first: clearing the flag before the overlay is gone lets a new
      // flow start while this one is still tearing its overlay down.
      await this.dismissGuestReconnectLoading();
      this.guestReconnectInFlight = false;
    }
  }

  private async runGuestReconnectOffer(opts?: {
    extraMessage?: string;
    ignoreSharedDismissal?: boolean;
  }): Promise<boolean> {
    const selectedTable: any = await this.getReconnectableDineInTable();
    if (!selectedTable) {
      return false;
    }

    // The reservation cart renders the same customer-info login component and
    // sets a dine-in table of its own, so a reservation customer could otherwise
    // be turned into an only_dinein guest. Every neighbouring path branches on
    // this flag — including a dedicated login popup variant — so this one does
    // too.
    const groupApp: any = await this.getGroupAppSnapshot();
    if (!groupApp || groupApp.active_reservation_app) {
      return false;
    }

    // Shared with the two existing guest-offer surfaces: once the customer has
    // said "not now", stop asking for ten minutes. Without it the same alert
    // reappears on every LOGIN and every CHECKOUT press.
    if (!opts?.ignoreSharedDismissal && (await this.isGuestOfferSuppressed())) {
      return false;
    }

    const storeInfo: any = await this.getStoreInfoSnapshot();
    if (!storeInfo || storeInfo.store_id == null || !storeInfo.group) {
      return false;
    }

    // Store opt-in only. NO location lookup here, deliberately.
    //
    // MobileOrder already has a dine-in presence check, and it is a different
    // thing from this one: deliveryMethodAddressSub runs checkDeviceLocation()
    // the moment dineIn + selectedDineInTable are set, gated on the store's own
    // dineinGpsCheck flag, at 300m, and it BLOCKS with an explaining modal.
    //
    // By the time this offer can fire, a table is already selected — so that
    // gate has already had its say. Re-checking here could only ever repeat a
    // decision already taken (dineinGpsCheck=true, the customer was accepted at
    // 300m) or override one taken deliberately (dineinGpsCheck=false, the store
    // asked for no location check at all). It cannot add information, and the
    // second threshold was even stricter than the first, so it could refuse a
    // customer the real gate had just admitted.
    //
    // The selected table IS the evidence: it carries connected_mobile and was
    // reached by scanning the QR on the table itself.
    if (!this.shouldAttemptLoginAssistForStore(storeInfo)) {
      return false;
    }

    this.reportGuestReconnect("offered", storeInfo, selectedTable);

    const answer = await this.presentGuestReconnectAlert(opts);
    if (answer !== "accepted") {
      // Only an explicit "not now" suppresses the offer, matching the existing
      // surface: the dismissal safety net must not silence all three offers for
      // ten minutes on a stray programmatic close.
      if (answer === "declined" && !opts?.ignoreSharedDismissal) {
        this.store.dispatch(
          new AuthActions.SetGuestOrderOfferDismissedAt(Date.now()),
        );
      }
      return false;
    }

    await this.presentGuestReconnectLoading();
    const reconnected = await this.authenticateGuestOnSelectedTable(
      storeInfo,
      selectedTable,
    );
    await this.dismissGuestReconnectLoading();

    return reconnected;
  }

  private async isGuestOfferSuppressed(): Promise<boolean> {
    const authState: any = await this.getAuthSnapshot();
    const dismissedAt = authState ? authState.guestOrderOfferDismissedAt : null;
    if (dismissedAt == null) {
      return false;
    }

    const tenMinutesMs = 10 * 60 * 1000;
    return Date.now() - dismissedAt < tenMinutesMs;
  }

  // The offer targets exactly one state: no session at all, cart already in
  // dine-in, and a table carrying the connected_mobile the guest auth needs. A
  // normal logged-in customer must never see it — accepting would replace their
  // real account with a guest session.
  private async getReconnectableDineInTable(): Promise<any> {
    // Both the user AND the token must be gone. While the token is still in the
    // store the session is either live or mid-hydration (only_dinein arrives with
    // get-customer-data), and every login flow passes through that shape — which
    // is why this cannot key on the user alone. Same predicate as the in-session
    // dangling probe in dinein-validations.service.
    const authState: any = await this.getAuthSnapshot();
    if (!authState || authState.user || authState.mo_token) {
      return null;
    }

    const deliveryMethodAddress: any =
      await this.getDeliveryMethodAddressSnapshot();
    if (!deliveryMethodAddress || deliveryMethodAddress.dineIn !== true) {
      return null;
    }

    const selectedTable = deliveryMethodAddress.selectedDineInTable;
    if (
      !selectedTable ||
      selectedTable.table_id == null ||
      !selectedTable.connected_mobile
    ) {
      return null;
    }

    return _.cloneDeep(selectedTable);
  }

  // Returns "accepted" | "declined" | "dismissed" — the caller has to tell an
  // explicit "not now" from a stray close, because only the former suppresses
  // the offer for the next ten minutes.
  private async presentGuestReconnectAlert(opts?: {
    extraMessage?: string;
  }): Promise<"accepted" | "declined" | "dismissed"> {
    let chosen: "accepted" | "declined" | null = null;

    const alert = await this.alertController.create({
      header: this.translateService.instant(
        "popups.magic-login-confirmation.guest-order-title",
      ),
      message:
        this.dineinTableLabelService.instant(
          "popups.magic-login-confirmation.continue-as-guest-on-selected-table",
        ) + (opts?.extraMessage ? "<br><br>" + opts.extraMessage : ""),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translateService.instant(
            "popups.magic-login-confirmation.not-now",
          ),
          role: "cancel",
          handler: () => {
            chosen = "declined";
          },
        },
        {
          text: this.translateService.instant(
            "popups.dine-in-modal.continue-as-guest",
          ),
          handler: () => {
            chosen = "accepted";
          },
        },
      ],
    });

    await alert.present();

    // Wait for the overlay to be GONE, not merely for the button press. Ionic
    // runs a button handler BEFORE it dismisses, so resolving from the handler
    // returned while the alert was still on screen — and the caller opens the
    // login modal the instant this returns. Presenting a modal into a
    // still-dismissing alert can leave login-popups-service holding its `true`
    // sentinel in FirebaseRecaptchaModal with no modal behind it, and since that
    // one field gates the whole LOGIN button, the button then does nothing for
    // the rest of the session. A backdrop/hardware-back dismissal also lands
    // here, which is why the answer defaults to "dismissed".
    await alert.onDidDismiss();

    return chosen ?? "dismissed";
  }

  private async authenticateGuestOnSelectedTable(
    storeInfo: any,
    selectedTable: any,
  ): Promise<boolean> {
    const device_id = await this.getDeviceId();
    const tablesPayload: any = await this.fetchOnlyDineInTablesPayload(
      storeInfo,
      device_id,
    );
    if (!tablesPayload || !tablesPayload.auth_key) {
      this.reportGuestReconnect("failed", storeInfo, selectedTable);
      return false;
    }

    // The alert is user-paced, so the state has to be re-read rather than
    // trusted: a session that appeared while it was open must win, and a table
    // the customer changed in the meantime must not be the one authenticated.
    // getReconnectableDineInTable re-applies the full guard, so this covers
    // both.
    const currentTable: any = await this.getReconnectableDineInTable();
    if (
      !currentTable ||
      selectedTable.table_id == null ||
      currentTable.table_id.toString() !== selectedTable.table_id.toString()
    ) {
      // Reported rather than bailed silently: the customer accepted, so an
      // "offered" row without a terminal event would leave the funnel this
      // feature exists to read with a dangling branch.
      this.reportGuestReconnect("aborted", storeInfo, selectedTable);
      return false;
    }

    // Authenticate against the table as the SERVER currently describes it, not
    // the copy the cart persisted. The dine-in modal has always taken the table
    // out of this same response and filtered on active, and a table that went
    // inactive or was re-assigned to a different mobile must not be re-claimed
    // from stale local state.
    const serverTable: any = _.find(tablesPayload.tables, (table: any) => {
      return (
        table &&
        table.table_id != null &&
        table.table_id.toString() === currentTable.table_id.toString()
      );
    });
    if (
      !serverTable ||
      !serverTable.active ||
      !serverTable.connected_mobile ||
      currentTable.connected_mobile == null ||
      serverTable.connected_mobile.toString() !==
        currentTable.connected_mobile.toString()
    ) {
      this.reportGuestReconnect("table_stale", storeInfo, currentTable);
      return false;
    }

    // Mirrors the dine-in modal: login_loading marks the hydration window that
    // follows, and no_login releases it whenever no session comes out of it.
    this.store.dispatch(new AuthActions.SetLoginState("login_loading"));

    const token = await this.fetchOnlyDineInUserToken(
      storeInfo,
      serverTable,
      tablesPayload.auth_key,
    );
    if (!token) {
      await this.releaseLoginStateIfStillLoggedOut();
      this.reportGuestReconnect("failed", storeInfo, currentTable);
      return false;
    }

    // Same handoff as the dine-in modal: the token alone drives hydration, and
    // the only_dinein user landing in the store re-applies the guest groupApp.
    this.store.dispatch(new AuthActions.SetMoToken(token));

    // But the token only *asks* for hydration: app.component reacts to it through
    // a 5s throttle with trailing:false, so a recent get-customer-data can drop
    // the request on the floor. Reporting success on the dispatch would tell the
    // caller the customer is back while the cart still shows LOGIN — worse than
    // the state being repaired. Wait for the user to actually land, and nudge
    // once past the throttle window before giving up.
    let user: any = await this.waitForHydratedUser(6000);
    if (!user) {
      this.store.dispatch(
        new AuthActions.SetTriggerGetUserDataTimestamp(Date.now()),
      );
      user = await this.waitForHydratedUser(8000);
    }

    if (!user) {
      // The budget can expire a hair before hydration lands, and the release
      // re-reads auth anyway — so use what it found instead of discarding it and
      // reporting a failure over a session that is live by now.
      user = await this.releaseLoginStateIfStillLoggedOut();
    }

    if (!user || !user.only_dinein) {
      this.reportGuestReconnect("no_hydration", storeInfo, currentTable);
      return false;
    }

    // Written only once the session is real, so a hydration that never completed
    // cannot leave a marker behind for the boot probe to report as dangling.
    this.writeDineInGuestMarker(currentTable);
    this.reportGuestReconnect("success", storeInfo, currentTable);

    return true;
  }

  private async waitForHydratedUser(timeoutMs: number): Promise<any> {
    const startedAt = Date.now();

    // only_dinein specifically, not any user: a normal login completing at the
    // same moment would otherwise be reported as a successful reconnect and get
    // a guest marker written for it. Same predicate the boot probe uses.
    while (Date.now() - startedAt < timeoutMs) {
      const authState: any = await this.getAuthSnapshot();
      if (authState && authState.user && authState.user.only_dinein) {
        return authState.user;
      }

      await this.delay(250);
    }

    // One last read after the budget expires, not just at the top of the loop:
    // delay() is a setTimeout, which a backgrounded WebView suspends, so a
    // customer who returns long after would otherwise be reported as never
    // hydrated while the session is in fact live.
    const settledAuthState: any = await this.getAuthSnapshot();
    return settledAuthState &&
      settledAuthState.user &&
      settledAuthState.user.only_dinein
      ? settledAuthState.user
      : null;
  }

  // Only release the login state while there is still no session. A late
  // hydration can land after the wait budget expired, and dispatching no_login
  // over a live user renders logged-out UI on top of a working session.
  // Returns the user it found, so a caller that raced a late hydration can use
  // it instead of re-reading. Self-contained try/catch because one of its
  // callers is the catch block above, where a throw would escape past both call
  // sites — and the read and the dispatch are guarded separately, so a failed
  // read still releases login_loading instead of stranding the UI on a spinner.
  private async releaseLoginStateIfStillLoggedOut(): Promise<any> {
    let user: any = null;

    try {
      const authState: any = await this.getAuthSnapshot();
      user = authState ? authState.user : null;
    } catch (e) {}

    if (user) {
      return user;
    }

    try {
      this.store.dispatch(new AuthActions.SetLoginState("no_login"));
    } catch (e) {}

    return null;
  }

  private async presentGuestReconnectLoading() {
    if (this.guestReconnectLoading) {
      return;
    }

    const message = this.translateService.instant("loading-please-wait");
    this.guestReconnectLoading = await this.loadingController.create({
      // 60s rather than the 30s of the visitor twin, because a spinner that
      // vanishes mid-flight is worse than no spinner at all. It still does not
      // cover the true worst case: each of the two calls carries
      // timeout(10000) + retry(2) — 30s apiece — plus up to 14s of hydration
      // wait, so ~74s. Bounding the flow itself (rather than the overlay) is
      // the real fix and is deliberately left as a follow-up.
      duration: 60000,
      message: message,
      cssClass: "ionicLoadingCss",
      keyboardClose: true,
    });

    return await this.guestReconnectLoading.present();
  }

  private async dismissGuestReconnectLoading() {
    if (!this.guestReconnectLoading) {
      return;
    }

    const loading = this.guestReconnectLoading;
    this.guestReconnectLoading = null;
    // Wrapped rather than .catch()'d: this runs inside a finally, which catch
    // does not cover, so a synchronous throw from dismiss() would escape past
    // both callers — the exact failure the try/catch exists to prevent.
    try {
      await loading.dismiss();
    } catch (e) {}
  }

  // Keeps res.tables alongside the auth key: the caller has to validate the
  // persisted table against the server's own list before authenticating on it.
  private async fetchOnlyDineInTablesPayload(
    storeInfo: any,
    device_id: any,
  ): Promise<any> {
    return await new Promise((resolve) => {
      this.dataStorageService
        .getDineInTables(storeInfo.group, storeInfo.store_id, device_id)
        .subscribe({
          next: (res: any) => {
            resolve(
              res && res.success
                ? {
                    auth_key: res.onlyDineInAuth || null,
                    tables: res.tables || [],
                  }
                : null,
            );
          },
          error: () => {
            resolve(null);
          },
        });
    });
  }

  private async fetchOnlyDineInUserToken(
    storeInfo: any,
    selectedTable: any,
    auth_key: any,
  ): Promise<any> {
    return await new Promise((resolve) => {
      this.dataStorageService
        .getOnlyDineInUserAuth(
          storeInfo.store_id,
          storeInfo.group,
          selectedTable.connected_mobile,
          selectedTable.table_id,
          auth_key,
        )
        .subscribe({
          next: (res: any) => {
            resolve(res && res.success ? res.token || null : null);
          },
          error: () => {
            resolve(null);
          },
        });
    });
  }

  // Mirrors the marker the dine-in modal writes on a successful guest auth, so a
  // session recovered here is visible to the same boot/in-session probes.
  private writeDineInGuestMarker(selectedTable: any) {
    try {
      if (selectedTable && selectedTable.table_id != null) {
        window.localStorage.setItem(
          "mo_dinein_active",
          JSON.stringify({
            table_id: selectedTable.table_id.toString(),
            ts: Date.now(),
          }),
        );
      }
    } catch (e) {}
  }

  // Reuses dinein_auth_result under its own source so the funnel (offered →
  // success/failed) is readable without touching the footprints consumer: every
  // field below already survives its dine-in _.pick.
  private reportGuestReconnect(
    result: string,
    storeInfo: any,
    selectedTable: any,
  ) {
    try {
      this.analyticsService.dmAnalyticsCustomMessage("dinein_auth_result", {
        store_id: storeInfo ? storeInfo.store_id : null,
        table_id:
          selectedTable && selectedTable.table_id != null
            ? selectedTable.table_id.toString()
            : null,
        source: "guest_reconnect",
        result: result,
        only_dinein_mode: true,
        selected_table_found: true,
      });
    } catch (e) {}
  }

  private async getAuthSnapshot(): Promise<any> {
    return await new Promise((resolve) => {
      this.store
        .select("auth")
        .pipe(first())
        .subscribe((state: any) => resolve(state || null))
        .unsubscribe();
    });
  }

  private async getGroupAppSnapshot(): Promise<any> {
    return await new Promise((resolve) => {
      this.store
        .select("groupApp")
        .pipe(first())
        .subscribe((state: any) => resolve(state?.groupApp || null))
        .unsubscribe();
    });
  }

  private async delay(ms: number): Promise<void> {
    return await new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, ms);
    });
  }

  private async getDeliveryMethodAddressSnapshot(): Promise<any> {
    return await new Promise((resolve) => {
      this.store
        .select("deliveryMethodAddress")
        .pipe(first())
        .subscribe((state: any) => resolve(state || null))
        .unsubscribe();
    });
  }

  private shouldAttemptLoginAssistForStoreInfo(storeInfo: any): boolean {
    return this.shouldAttemptLoginAssistForStore(storeInfo);
  }

  private shouldAttemptLoginAssistForStore(storeInfo: any): boolean {
    return !!(
      storeInfo &&
      storeInfo.dinein === true &&
      storeInfo.notifyUserForOnlyDineinOnLogin === true &&
      storeInfo.store_id &&
      storeInfo.group &&
      storeInfo.mapCenterStoreLat != null &&
      storeInfo.mapCenterStoreLng != null
    );
  }

  private getStoreIdKey(storeInfo: any): string | null {
    return storeInfo && storeInfo.store_id != null ? String(storeInfo.store_id) : null;
  }

  private async getStoresSnapshot(): Promise<any[]> {
    return await new Promise((resolve) => {
      this.store
        .select("stores")
        .pipe(first())
        .subscribe((state: any) => resolve(state?.stores || []))
        .unsubscribe();
    });
  }

  private async getStoreInfoSnapshot(): Promise<any | null> {
    return await new Promise((resolve) => {
      this.store
        .select(selectors.getStoreInfo)
        .pipe(first())
        .subscribe((storeInfo: any) => resolve(storeInfo || null))
        .unsubscribe();
    });
  }

  private initCacheIfNeeded(storeIdKey: string) {
    if (!this.cacheByStoreId[storeIdKey]) {
      this.cacheByStoreId[storeIdKey] = {
        onlyDineInCapability: null,
        onlyDineInCapabilityFetchedAt: null,
        distanceToStoreMeters: null,
        distanceFetchedAt: null,
        gpsError: null,
        gpsDenied: null,
      };
    }
  }

  private async getDeviceId(): Promise<any> {
    return new Promise((resolve) => {
      this.store
        .select("platformInfo")
        .pipe(first())
        .subscribe((state: any) => {
          resolve(state?.device_id || null);
        })
        .unsubscribe();
    });
  }

  private async prefetchOnlyDineInCapability(storeInfo: any): Promise<boolean> {
    const storeIdKey = this.getStoreIdKey(storeInfo);
    if (!storeIdKey) {
      return false;
    }
    this.initCacheIfNeeded(storeIdKey);

    if (this.cacheByStoreId[storeIdKey].onlyDineInCapabilityFetchedAt != null) {
      this.log("capability cache hit", {
        store_id: storeIdKey,
        capability: this.cacheByStoreId[storeIdKey].onlyDineInCapability,
      });
      return this.cacheByStoreId[storeIdKey].onlyDineInCapability === true;
    }

    if (this.capabilityInFlightByStoreId[storeIdKey]) {
      this.log("capability in-flight await", { store_id: storeIdKey });
      return await this.capabilityInFlightByStoreId[storeIdKey];
    }

    this.capabilityInFlightByStoreId[storeIdKey] = (async () => {
      const device_id = await this.getDeviceId();
      this.log("capability fetch start", {
        store_id: storeIdKey,
        device_id,
      });
      return await new Promise<boolean>((resolve) => {
        this.dataStorageService
          .getDineInTables(storeInfo.group, storeInfo.store_id, device_id)
          .subscribe({
            next: (res: any) => {
              const tables = res && res.success ? res.tables || [] : [];
              const capability = _.some(tables, (table) => {
                return !!(table && table.active && table.connected_mobile);
              });

              this.log("capability fetch success", {
                store_id: storeIdKey,
                tablesCount: tables?.length ?? 0,
                capability,
                hasOnlyDineInAuth: !!res?.onlyDineInAuth,
              });
              this.cacheByStoreId[storeIdKey].onlyDineInCapability =
                capability;
              this.cacheByStoreId[storeIdKey].onlyDineInCapabilityFetchedAt =
                Date.now();
              resolve(capability);
            },
            error: () => {
              this.log("capability fetch error", { store_id: storeIdKey });
              this.cacheByStoreId[storeIdKey].onlyDineInCapability = false;
              this.cacheByStoreId[storeIdKey].onlyDineInCapabilityFetchedAt =
                Date.now();
              resolve(false);
            },
          });
      });
    })().finally(() => {
      this.capabilityInFlightByStoreId[storeIdKey] = null;
    });

    return await this.capabilityInFlightByStoreId[storeIdKey];
  }

  private async prefetchGpsDistanceToStore(storeInfo: any): Promise<void> {
    const storeIdKey = this.getStoreIdKey(storeInfo);
    if (!storeIdKey) {
      return;
    }
    this.initCacheIfNeeded(storeIdKey);

    if (this.cacheByStoreId[storeIdKey].distanceFetchedAt != null) {
      this.log("gps cache hit", {
        store_id: storeIdKey,
        distanceToStoreMeters: this.cacheByStoreId[storeIdKey].distanceToStoreMeters,
        gpsError: this.cacheByStoreId[storeIdKey].gpsError,
        gpsDenied: this.cacheByStoreId[storeIdKey].gpsDenied,
      });
      return;
    }

    if (this.gpsInFlightByStoreId[storeIdKey]) {
      this.log("gps in-flight await", { store_id: storeIdKey });
      await this.gpsInFlightByStoreId[storeIdKey];
      return;
    }

    this.gpsInFlightByStoreId[storeIdKey] = (async () => {
      this.log("gps fetch start", {
        store_id: storeIdKey,
        mapCenterStoreLat: storeInfo?.mapCenterStoreLat,
        mapCenterStoreLng: storeInfo?.mapCenterStoreLng,
      });
      const distance_object: any =
        await this.checkLocationDistanceService.calculateDistanceFromGps(
          {
            lat: storeInfo.mapCenterStoreLat,
            lng: storeInfo.mapCenterStoreLng,
          },
          false,
        );

      if (distance_object && distance_object.distance != null) {
        this.log("gps fetch success", {
          store_id: storeIdKey,
          distanceToStoreMeters: distance_object.distance,
        });
        this.cacheByStoreId[storeIdKey].distanceToStoreMeters =
          distance_object.distance;
        this.cacheByStoreId[storeIdKey].distanceFetchedAt = Date.now();
        this.cacheByStoreId[storeIdKey].gpsError = null;
        this.cacheByStoreId[storeIdKey].gpsDenied = null;
      } else {
        this.log("gps fetch error", {
          store_id: storeIdKey,
          error: distance_object?.error ?? "gps_general_error",
        });
        this.cacheByStoreId[storeIdKey].distanceToStoreMeters = null;
        this.cacheByStoreId[storeIdKey].distanceFetchedAt = Date.now();
        this.cacheByStoreId[storeIdKey].gpsError =
          distance_object && distance_object.error
            ? String(distance_object.error)
            : "gps_general_error";
        this.cacheByStoreId[storeIdKey].gpsDenied =
          this.cacheByStoreId[storeIdKey].gpsError ===
          "popups.order-now-popup.please-enable-gps-in-order-to-check-your-position";
        this.log("gps denied?", {
          store_id: storeIdKey,
          gpsDenied: this.cacheByStoreId[storeIdKey].gpsDenied,
          gpsError: this.cacheByStoreId[storeIdKey].gpsError,
        });
      }
    })().finally(() => {
      this.gpsInFlightByStoreId[storeIdKey] = null;
    });

    await this.gpsInFlightByStoreId[storeIdKey];
  }

  private getStoresWithinDistanceMeters(
    stores: any[],
    maxDistanceMeters: number,
    opts?: { maxAgeMs?: number },
  ): any[] {
    const maxAgeMs = opts?.maxAgeMs ?? 2 * 60 * 1000;

    const withDistance = _.filter(stores, (storeInfo) => {
      const storeIdKey = this.getStoreIdKey(storeInfo);
      if (!storeIdKey) {
        return false;
      }
      const cache = this.cacheByStoreId[storeIdKey];
      if (!cache || cache.distanceToStoreMeters == null || cache.distanceFetchedAt == null) {
        return false;
      }
      if (Date.now() - cache.distanceFetchedAt > maxAgeMs) {
        return false;
      }
      return cache.distanceToStoreMeters <= maxDistanceMeters;
    });

    return _.orderBy(
      withDistance,
      [
        (s) => {
          const key = this.getStoreIdKey(s);
          return key && this.cacheByStoreId[key]
            ? this.cacheByStoreId[key].distanceToStoreMeters
            : Number.MAX_SAFE_INTEGER;
        },
      ],
      ["asc"],
    );
  }

  private async prefetchGpsDistancesToStores(
    stores: any[],
    opts?: { maxAgeMs?: number },
  ): Promise<void> {
    const maxAgeMs = opts?.maxAgeMs ?? 2 * 60 * 1000;

    const storeIdsNeedingRefresh = _.filter(stores, (storeInfo) => {
      const key = this.getStoreIdKey(storeInfo);
      if (!key) {
        return false;
      }
      this.initCacheIfNeeded(key);
      const fetchedAt = this.cacheByStoreId[key].distanceFetchedAt;
      if (fetchedAt == null) {
        return true;
      }
      return Date.now() - fetchedAt > maxAgeMs;
    });

    if (!storeIdsNeedingRefresh.length) {
      return;
    }

    if (this.allStoresGpsInFlight) {
      await this.allStoresGpsInFlight;
      return;
    }

    this.allStoresGpsInFlight = (async () => {
      this.log("gps(all stores) fetch start", {
        storesCount: stores?.length ?? 0,
      });

      const res: any =
        await this.checkLocationDistanceService.calculateDistanceFromAllStoresByGPS(
          stores,
          false,
          {
            applyDistanceFactor: false,
            includeDistanceToStoreMeters: true,
            writeStoresToState: false,
            timeoutMs: 20000,
          },
        );

      const now = Date.now();

      if (res && res.sucess && res.stores) {
        this.log("gps(all stores) fetch success", {
          storesCount: res.stores?.length ?? 0,
        });

        _.each(res.stores, (storeFromGps) => {
          const key = this.getStoreIdKey(storeFromGps);
          if (!key) {
            return;
          }
          this.initCacheIfNeeded(key);
          this.cacheByStoreId[key].distanceToStoreMeters =
            storeFromGps && storeFromGps.distanceToStoreMeters != null
              ? Number(storeFromGps.distanceToStoreMeters)
              : null;
          this.cacheByStoreId[key].distanceFetchedAt = now;
          this.cacheByStoreId[key].gpsError = null;
          this.cacheByStoreId[key].gpsDenied = null;
        });
        return;
      }

      const errorKey = res && res.error ? String(res.error) : "gps_general_error";
      const gpsDenied =
        errorKey ===
        "popups.order-now-popup.please-enable-gps-in-order-to-check-your-position";

      this.log("gps(all stores) fetch error", {
        error: errorKey,
        gpsDenied,
      });

      _.each(stores, (storeInfo) => {
        const key = this.getStoreIdKey(storeInfo);
        if (!key) {
          return;
        }
        this.initCacheIfNeeded(key);
        this.cacheByStoreId[key].distanceToStoreMeters = null;
        this.cacheByStoreId[key].distanceFetchedAt = now;
        this.cacheByStoreId[key].gpsError = errorKey;
        this.cacheByStoreId[key].gpsDenied = gpsDenied;
      });
    })().finally(() => {
      this.allStoresGpsInFlight = null;
    });

    await this.allStoresGpsInFlight;
  }
}
